/**
 * The vocabulary of a work's recorded history, and of the attestations made
 * about it.
 *
 * PROVENANCE IS THE WHOLE HISTORY, not only the chain of title. It was only the
 * chain until 20260820090100: one row per holder, and an exhibition, a
 * publication or a condition report had nowhere to go. `ProvenanceEntryKind` is
 * the discriminator that opened it, and the two kinds that move title are what
 * is left of the older, narrower meaning.
 *
 * These values must stay in step with the enums in
 * supabase/migrations/20260804090500_provenance.sql,
 * supabase/migrations/20260820090100_artwork_history.sql and
 * supabase/migrations/20261004097000_work_insurance_history.sql. Nothing
 * enforces that but this comment — the same standing arrangement as
 * `domain/registration.ts`.
 *
 * What each kind is made of is docs/artworkHistory.md.
 */
/**
 * What a piece of history is ABOUT. The registry calls all nine provenance.
 *
 * "Artwork history" and "provenance" are one word in this domain and always
 * were — the collector's timeline, the chain of title and the exhibition record
 * are the same append-only list read three ways. Which is why this is a
 * discriminator on `provenance_entries` rather than four sibling tables: an
 * exhibition and a sale are confirmed by the same people through the same
 * attestation, reach the same feeds, and are frozen by the same rule.
 *
 * ORDERED AS A WORK'S LIFE RUNS, not as the modal draws them. The chip order in
 * the client is a layout decision and lives in the client.
 *
 * There is no `ownership` value and that is deliberate: an entry that named a
 * holder without saying how they came to hold it was the old shape, and the two
 * kinds below that carry title each name the transaction that moved it. A
 * holding with no known transaction is a `sale` with `method: 'unknown'`, which
 * is what every row written before this enum existed became.
 */
export const PROVENANCE_ENTRY_KINDS = [
    /** Title changed hands. Carries the new holder, and how they came by it. */
    "sale",
    /** Title changed hands at a named house, with a sale and a lot number. */
    "auction",
    /** Possession moved and title did not. */
    "loan",
    /** The work was shown. */
    "exhibition",
    /** Where the object physically was. */
    "location",
    /**
     * A significant publication the work appears in, and where in it.
     *
     * The CLIENT says "significant publication"; the value stays `bibliography`.
     * OND-38 settled both halves: the citation is free text on this kind rather
     * than a structured pointer into somebody else's catalogue, because ONDO "is
     * meant to become the catalogue raisonné for everything" — and renaming the
     * value would blank the kind on every feed line already written. See
     * `20261014090100_a_significant_publication_is_the_citation.sql`.
     */
    "bibliography",
    /** A paper put on the record in its own right. */
    "document",
    /** A dated assessment by a named conservator. */
    "condition",
    /**
     * Who made the work — "this work is by Erwin Wurm" — with its evidence.
     *
     * THE ONE KIND WHOSE PURPOSE IS TO BE ANSWERED. Every other kind records
     * something that happened and may be confirmed; this one exists so an
     * authorised studio, estate or foundation can accept or reject the authorship
     * claim itself, through the same request flow a loan uses. OND-20.
     *
     * It is deliberately NOT `ondo.works.attributed_name`, which is whatever the
     * registering party typed and is editable. The claim on an entry is frozen,
     * so an entry a studio confirmed keeps saying what the studio confirmed.
     *
     * It carries the claim, its dates, the author's evidence claim and notes, and
     * none of the other kinds' columns — a sale price on an authorship claim would
     * be a different claim. See `20261004096000_artist_attribution.sql`.
     */
    "attribution",
    /**
     * Who insured the work, under what policy, for how much, and when.
     *
     * LAST, because a work's life does not run through insurance at a particular
     * moment — a policy sits alongside everything else that happened to the
     * object rather than at a point in its story. Appended to the Postgres enum
     * for the same reason, and the order has to match: 20261004097000.
     *
     * Overlapping periods are allowed and are not a mistake to be reconciled. Two
     * policies may cover the same months, a renewal is a new entry rather than an
     * edit of the one it renews, and each period gathers confirmations and
     * disputes of its own.
     */
    "insurance",
];
/**
 * What has been said about a work's authorship claim, and by whom.
 *
 * **Must stay in step with `ondo.attribution_state`, and nothing enforces
 * that** — the Postgres enum is the authority and this array is the copy a
 * screen switches on. `20261014090000` is the migration that last changed it.
 *
 * DERIVED ON EVERY READ, never stored, and that is load-bearing rather than
 * incidental: `ondo.works.verification` was dropped in August precisely so the
 * registry could not hold a verdict every reader had to remember to filter. The
 * states come out of `ondo.work_attribution()`, which reads the attribution
 * entry's attestations.
 *
 * `not_requested` COVERS TWO SITUATIONS and a screen may want to tell them
 * apart: no authorship claim on the record at all, and a claim nobody has been
 * asked about. To a collector they are the same fact — nobody has asked the
 * studio — which is why that is one state and not two.
 *
 * SILENCE IS NEITHER ACCEPTANCE NOR REJECTION. `awaiting` is a live request and
 * nothing ages out of it, because there is no timer anywhere in this flow and
 * OND-87 forbids inventing one. A request open for three years reads `awaiting`.
 *
 * AN ANSWER THAT TOOK NO POSITION — declined, no_recall, referred, withdrawn —
 * leaves the state at `not_requested`, which is why
 * `WorkAttribution.lastOutcome` exists beside it. A screen that showed a shrug
 * as a verdict would be reporting the opposite of what happened.
 *
 * AND AN ANSWER FROM A PARTY ONDO NEVER APPOINTED IS ITS OWN PAIR OF READINGS —
 * OND-215. A collector may ask a studio at an email address nobody holds; ONDO
 * mints a dormant studio, somebody signs up with the address and claims it, and
 * they can then answer. That answer is real and durable, and it carries no
 * appointment: `ondo.appoint_authority()` requires the registry's own key, and
 * claiming a studio confers nothing. So the two `_unappointed` readings exist to
 * SHOW such an answer without promoting it — "the question moves from 'is it
 * true' to 'who said it'", which is what provenance does everywhere else here.
 * They must never be folded into `confirmed` or `disputed`.
 */
export const ATTRIBUTION_STATES = [
    /** Nobody has been asked. The state almost every work is in. */
    "not_requested",
    /** A live request is with somebody, unanswered — appointed or not. */
    "awaiting",
    /**
     * Somebody ONDO has NOT appointed for this artist put their name to the
     * claim. Weaker than `confirmed` and never equal to it: it issues no
     * catalogue number, turns no code green and earns the answerer no fee.
     */
    "confirmed_unappointed",
    /** An authority ONDO appointed for this artist put its name to the claim. */
    "confirmed",
    /**
     * Somebody ONDO has NOT appointed for this artist put their name against the
     * claim. Shown for the same reason the confirmation is, and not read as a
     * contested attribution by any public surface.
     */
    "disputed_unappointed",
    /**
     * An authority ONDO appointed put its name against it. Outranks that
     * authority's own tier's confirmation.
     */
    "disputed",
];
/**
 * Whether somebody has actually answered the authorship claim.
 *
 * The predicate a screen uses to decide whether to print a name, and
 * deliberately not `state !== "not_requested"`: `awaiting` has no answer and no
 * name to print either.
 *
 * TRUE FOR THE UNAPPOINTED READINGS TOO — OND-215. Somebody answered, there is
 * a name on it, and a screen withholding the name of whoever gave the weaker
 * answer would be hiding the half that makes it weaker. Whether the answer is
 * AUTHORITATIVE is a different question and `attributionAppointed` is it.
 */
export function attributionAnswered(state) {
    return (state === "confirmed" ||
        state === "disputed" ||
        state === "confirmed_unappointed" ||
        state === "disputed_unappointed");
}
/**
 * Whether the answer came from an authority ONDO appointed for the claimed
 * artist — OND-215.
 *
 * The question every door that acts on an attribution asks, kept apart from
 * `attributionAnswered` because conflating the two is the bug OND-215 closed
 * from the other side. A catalogue number, a green code and a fee all turn on
 * this one and not on whether anybody spoke.
 */
export function attributionAppointed(state) {
    return state === "confirmed" || state === "disputed";
}
/**
 * Whether this kind moved title. The two that do ARE the chain of title.
 *
 * The one predicate that recovers the old, narrower meaning of provenance from
 * the new one, and the reason the widening cost nothing: a reader who wants the
 * chain filters on this, and everything the registry used to say about a chain
 * still holds over exactly that subset.
 */
export function movesTitle(kind) {
    return kind === "sale" || kind === "auction";
}
export function isProvenanceEntryKind(value) {
    return (typeof value === "string" &&
        PROVENANCE_ENTRY_KINDS.includes(value));
}
/**
 * How a holder came by the work. `sale` only — see the check constraint.
 *
 * Note what this is NOT: a kind. `auction` appears in both lists and means
 * different things in each — here it is how a purchase was transacted, there it
 * is an entry with a house, a sale title and a lot number. A `sale` entry with
 * `method: 'auction'` is somebody saying they bought at auction without naming
 * the sale; an `auction` entry is the sale itself.
 */
export const PROVENANCE_METHODS = [
    "created_by_artist",
    "purchase",
    "commission",
    "gift",
    "bequest",
    "inheritance",
    "exchange",
    "auction",
    "consignment",
    "restitution",
    "unknown",
];
/**
 * What the AUTHOR of an entry says about their own evidence for it.
 *
 * Read the names carefully: none of them is "verified". `verification` is a word
 * the registry owns — `WorkVerification` is what an appointed authority decided —
 * and a submitter's self-description borrowing it would be taken for the
 * registry's finding, because one word doing two jobs is always taken for the
 * weaker one.
 *
 * `documented` means the person typing it says they hold the paper. It is a
 * claim exactly as a `RegistrationEntry.body` is a claim. What the registry
 * itself has to offer about a provenance line is `verifiedByCount` — who put
 * their own name behind it.
 */
export const PROVENANCE_EVIDENCE_CLAIMS = [
    "documented",
    "secondary_literature",
    "owner_claim",
    "unverified",
];
/** A position a principal took on an entry. One live position each. */
export const ATTESTATION_STANCES = ["verify", "dispute"];
/**
 * How a verification request ended.
 *
 * Not a boolean, for the reason `ReviewOutcome` is not one. Four of these are
 * answers the asked party stands behind and their read of the entry survives;
 * `withdrawn` and `expired` end a request that produced nothing and take the
 * read back with it. That split is what `wasAnswered` below is for, and it
 * mirrors `ondo.asked_provenance_entry_ids()`.
 *
 * `no_recall` and `referred` arrived with 20260808085900. Neither is a decline:
 * one is somebody saying they looked and do not remember, which is information
 * the asker did not have; the other is somebody naming a better-placed party.
 */
export const VERIFICATION_REQUEST_OUTCOMES = [
    "attested",
    "declined",
    "no_recall",
    "referred",
    "withdrawn",
    "expired",
];
/**
 * What a request points at.
 *
 * `entry` IS THE DEFAULT AND MEANS THE WHOLE OF WHAT THE ENTRY SAYS. It arrived
 * with 20260820090000 because `ownership` had been carrying that job, and once
 * an entry could be an exhibition or a condition report, "do you confirm this
 * ownership?" was a question the registry was asking about a record that
 * asserted no ownership at all. The two narrower title aspects stay, and stay
 * meaningful, on the two kinds that move title.
 *
 * This list also used to end with a sentence saying an aspect for exhibitions
 * or condition would be a label over an empty table. That was true and is not:
 * `provenance_entries.kind` holds both. What has NOT changed is why there is no
 * aspect per kind — an aspect names a part of an entry to look at, and for seven
 * of the nine kinds the entry is all there is to look at. An insurance period is
 * one of the seven: a policy is confirmed whole, and an insurer asked about
 * 'the sum but not the dates' is being asked to half-sign a contract.
 *
 * A document-scoped answer produces NO attestation — the position lives on the
 * request row. See `ondo.answer_verification_request()`: each principal holds
 * one live position per entry, and spending it on an invoice would make being
 * asked about the link and about its evidence at once unanswerable.
 */
export const VERIFICATION_ASPECTS = [
    "entry",
    "ownership",
    "acquisition",
    "document",
];
/**
 * The four ways to answer, as the inbox offers them.
 *
 * Deliberately not `AttestationStance` plus some flags. A stance is a position
 * on a claim; two of these are refusals to take one, and flattening them would
 * make "I don't remember" indistinguishable from "no" at the type level, which
 * is exactly the conflation `no_recall` exists to prevent.
 */
export const VERIFICATION_ANSWERS = [
    "confirm",
    "dispute",
    "no_recall",
    "decline",
];
/**
 * What sort of paper a provenance document is.
 *
 * ELEVEN VALUES SINCE OND-62, and the two that arrived are the two the estate
 * walkthrough named and the registry had nowhere to file: an import
 * certificate, which was going in as `shipping_record` or `other`, and a
 * provenance declaration — the dealer's own written account of where a work has
 * been — which was going in as `other`. Both are the paper an estate looks at
 * hardest, and both arrived labelled as something else.
 *
 * ORDERED AS THE DATABASE ORDERS IT, with the two new values before `other`
 * rather than after. `other` means "none of the above", and a dropdown
 * offering two specific categories below it would be a list the client and the
 * enum disagreed about. `20261004098000_two_more_document_categories.sql`
 * decision 1.
 *
 * `provenance_declaration` is a DOCUMENT kind and not an entry kind, and the
 * two are easy to confuse: `PROVENANCE_ENTRY_KINDS` has `document`, and such an
 * entry carries a `documentKind` of its own. So a declaration can be filed
 * either as the kind of a paper attached to the claim it evidences, or as an
 * entry in its own right when the declaration IS the record.
 */
export const PROVENANCE_DOCUMENT_KINDS = [
    "invoice",
    "receipt",
    "certificate",
    "photograph",
    "correspondence",
    "exhibition_catalogue",
    "archival_record",
    "shipping_record",
    "import_certificate",
    "provenance_declaration",
    "other",
    /**
     * An insurance policy or its schedule.
     *
     * Not `certificate`, which in this vocabulary means somebody's word about the
     * work itself; a policy is a commercial contract about its value. And not
     * `other`, which loses the one fact a reader of the list wants. 20261004097000
     * decision 8 — appended to the Postgres enum, so it is last here too.
     */
    "policy",
];
/**
 * What the `provenance` STORE will take, and the one place it is written down
 * for every client.
 *
 * Three things declare this bucket and they are not interchangeable with the
 * two that hold PHOTOGRAPHS: the check constraint on
 * `ondo.provenance_documents.content_type`
 * (`20260804090500_provenance.sql`), the `[storage.buckets.provenance]` section
 * of `supabase/config.toml`, and whatever the hosted project actually enforces
 * — which `local-admin storage:check` compares against the second (OND-32).
 *
 * **IT IS A SEPARATE BUCKET WITH A SMALLER CEILING, AND THAT IS THE POINT.**
 * `registrations` and `works` take 50MiB of photograph and no PDF; this takes
 * 25MiB and a PDF. OND-147: "Documents live in a different, more restricted
 * store than work photographs. The app must not treat them interchangeably."
 * The phone did: `works/documentUpload.ts` weighed a scan against
 * `files.ts`'s `MAX_BYTES`, which is the WORK photograph's 50MB, so a 30MB
 * contract picked out of iCloud Drive passed every check this app makes, had
 * its row written, and was refused by storage — leaving the uploader a sentence
 * telling them to try again at something that could never succeed.
 *
 * So the figure lives HERE, beside the vocabulary, rather than once per client:
 * the mobile uploader, the web uploader's `MAX_DOCUMENT_BYTES` and the API's own
 * `byteSize` bound were three copies of 25MB, and a bucket narrowed to 10MiB
 * tomorrow would leave all three claiming otherwise.
 *
 * **AND IT IS CHECKED RATHER THAN COMMENTED**, which is the one place this file
 * departs from its own habit of "say so in a comment; nothing enforces it".
 * `bucket-contract.test.ts` already parses the real `config.toml` to prove the
 * drift checker reads it; it now asserts the declared bucket agrees with these
 * two constants. The declaration is machine-readable, so the comment did not
 * have to be the enforcement.
 *
 * The rejected alternative was reading `config.toml` at build time and emitting
 * these. It would make the figure impossible to disagree with and impossible to
 * SEE: a client bundle would carry a number no reader of this file could find,
 * and the mobile app cannot read the repository at runtime at all.
 */
export const PROVENANCE_DOCUMENT_CONTENT_TYPES = [
    "application/pdf",
    "image/jpeg",
    "image/png",
    "image/tiff",
    "image/heic",
    "image/webp",
];
/**
 * The most bytes one provenance document may be: 25MiB, as the bucket says.
 *
 * Binary megabytes, because the Supabase CLI parses `file_size_limit = "25MiB"`
 * RAM-style and applies it to the local stack — see `parseByteSize`. A client
 * holding 25,000,000 would refuse a file the bucket accepts, which is the
 * harmless direction, and 25 × 1000 × 1000 is not what any of the three
 * declarations mean.
 *
 * **A CLIENT MAY ONLY EVER NARROW THIS.** Widening it here to fit a photograph
 * would be a client claiming a ceiling the store does not have, and the store
 * is the one that refuses.
 */
export const MAX_PROVENANCE_DOCUMENT_BYTES = 25 * 1024 * 1024;
/**
 * HOW AN ENTRY IS CONTESTED, and never whether it is true.
 *
 * OND-62's third state. A position used to attach one level up, to a whole
 * entry, with one live stance per party — so an estate working through a dossier
 * of four papers had two buttons for all four and both answers were false:
 * disputing marked the genuine invoice disputed alongside the forgery, and
 * confirming put the studio's name behind the forged certificate. A rejected
 * document now makes the claim it hangs on QUESTIONABLE, which marks the entry
 * contested without anybody having to dispute the whole of it.
 *
 * THERE ARE THREE VALUES AND NOT FOUR, AND THE MISSING ONE IS `confirmed`.
 * docs/artworkHistory.md: the registry "reports `verifiedByCount` and
 * `disputedByCount` and concludes nothing. There is no threshold, no quorum and
 * no `isVerified`." A value saying an entry is confirmed would be that
 * threshold arriving as an enum — one signature would have to be enough, or
 * some number would — so every value here states only what is ON THE RECORD
 * against an entry, and `verifiedByCount` beside it remains the only thing that
 * speaks FOR a claim.
 *
 * Ordered weakest to strongest, which is the precedence
 * `ondo.provenance_entry_standing()` applies: a party who disputes the claim
 * has said something strictly stronger than one who rejects a single paper, and
 * `questionable` outranks any number of confirmations because the contested
 * position is the one a reader must not miss.
 *
 * Must stay in step with `ondo.provenance_standing` in
 * supabase/migrations/20261004098100_a_verdict_on_one_document.sql. Nothing
 * enforces that but this comment.
 */
export const PROVENANCE_STANDINGS = [
    /**
     * No live dispute of either kind. NOT an endorsement — an entry nobody has
     * looked at is uncontested.
     */
    "uncontested",
    /** Nobody disputes the entry, and a live verdict rejects one of its papers. */
    "questionable",
    /** Somebody's live position disputes the entry itself. */
    "disputed",
];
export function isProvenanceStanding(value) {
    return (typeof value === "string" &&
        PROVENANCE_STANDINGS.includes(value));
}
/**
 * Whether anything on the record stands against this entry.
 *
 * The one predicate a screen needs, so "is there something here to look at" is
 * answered in one place rather than by two string comparisons per caller. It
 * deliberately does NOT distinguish the two ways of being contested: which one
 * it is decides what the row SAYS, and that is the standing itself.
 */
export function isContested(standing) {
    return standing !== "uncontested";
}
/** Still waiting on the party who was asked. */
export function isLiveRequest(outcome) {
    return outcome === null;
}
/**
 * The asked party answered, one way or another.
 *
 * This is also the predicate that says their READ of the entry survived, which
 * is why declining counts: a body that declined must be able to show what it was
 * shown, and a body that passed it on must be able to show what it passed.
 * Mirrors `ondo.asked_provenance_entry_ids()` — not the attesting set, which is
 * now simply everyone who can read.
 */
export function wasAnswered(outcome) {
    return (outcome === "attested" ||
        outcome === "declined" ||
        outcome === "no_recall" ||
        outcome === "referred");
}
/**
 * The request is over, however it ended.
 *
 * Distinct from `wasAnswered` on purpose: a withdrawn request is resolved and
 * was never answered, and an inbox that used one predicate for both would file
 * "the asker changed their mind" under things this account did.
 */
export function isResolved(outcome) {
    return outcome !== null;
}
/**
 * Whether an answer put a name on the record.
 *
 * `no_recall` and `decline` end a request and stake nothing, which is why they
 * move neither `verifiedByCount` nor `disputedByCount`. A screen that showed
 * them as positions would be reporting a shrug as a signature.
 */
export function isPosition(answer) {
    return answer === "confirm" || answer === "dispute";
}
/**
 * Whether an entry is still standing.
 *
 * An entry is added or withdrawn and never edited, so this is the whole of its
 * lifecycle — there is no status to filter on and nothing in between.
 */
export function isLiveEntry(endedAt) {
    return endedAt === null;
}
//# sourceMappingURL=provenance.js.map